import { KEY_NAME, useKeyboardEventCallback } from '@cosuno/react-hooks';
import { useCallback, useContext, useEffect, useState } from 'react';

import { useScrollLock } from '#@/modules/scrollLock';
import useQueryMatches from '#@/shared/hooks/useQueryMatches';

import {
  CONTENT_MARGIN_DESKTOP,
  CONTENT_MARGIN_MOBILE,
  DEFAULT_SCALE,
  SCALE_FACTOR,
} from '../constants';
import { LightboxContext } from '../LightboxContext';
import { type ScaleValues, STATUS } from '../types';
import type { Dimensions, LightboxControls } from './types';

const useLightboxControls = (): LightboxControls => {
  const { initialScale, status, close, setIsLoading, setStatus } = useContext(LightboxContext);

  const [page, setPage] = useState(1);
  const [numPages, setNumPages] = useState(1);

  const [scale, setScale] = useState<ScaleValues>(initialScale ?? DEFAULT_SCALE);

  const [contentContainerSize, setContentContainerSize] = useState<Dimensions | null>(null);

  const { isMobile } = useQueryMatches();
  const contentMargin = isMobile ? CONTENT_MARGIN_MOBILE : CONTENT_MARGIN_DESKTOP;

  const previousPage = () => {
    if (page === 1) return;
    setPage(page - 1);
  };

  const nextPage = () => {
    if (page === numPages) return;
    setPage(page + 1);
  };

  const updateScale = useCallback((scaleToSet: Partial<ScaleValues>) => {
    setScale((prevScale) => ({ ...prevScale, ...scaleToSet }));
  }, []);

  const increaseScale = () => {
    updateScale({ current: scale.current * SCALE_FACTOR });
  };

  const decreaseScale = () => {
    updateScale({ current: scale.current * (1 / SCALE_FACTOR) });
  };

  const resetScale = useCallback(() => {
    updateScale(initialScale ?? DEFAULT_SCALE);
  }, [initialScale, updateScale]);

  useEffect(() => {
    if (status === STATUS.opening) {
      resetScale();

      setStatus(STATUS.open);
    }
  }, [status, setStatus, setIsLoading, resetScale]);

  useEffect(() => {
    if (status === STATUS.closing) {
      setContentContainerSize(null);
      resetScale();
      setPage(1);
      setNumPages(1);

      setStatus(STATUS.closed);
    }
  }, [status, setStatus, resetScale]);

  useKeyboardEventCallback(document, KEY_NAME.escape, close, {
    condition: status !== STATUS.closed,
    stopPropagation: true,
  });

  useScrollLock(status === STATUS.open, 'lightbox');

  return {
    setPage,
    setNumPages,
    previousPage,
    nextPage,
    setScale: updateScale,
    increaseScale,
    decreaseScale,
    resetScale,
    setContentContainerSize,
    page,
    numPages,
    scale,
    initialScale,
    contentMargin,
    contentContainerSize,
  };
};

export default useLightboxControls;
